// # Please ensure understanding of the pricing page public setup, documented here:
// # https://product.hubteam.com/docs/growth-ssm-handbook/docs/dotcom/overview.html
// # before making changes to this file or merging in mothership changes

import { Application } from 'foundations-components/components/Application';
import { Flex } from 'foundations-components/components/Flex';
import { createBrowserHistory } from 'history';
import { renderRoot } from 'quartz-core/client';
import { Router } from 'react-router-dom';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import BasicPublicI18nSetup from 'I18n/init/setup/BasicPublicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import Raven from 'raven-js';
import { DataFetchingClientProvider } from 'data-fetching-client';
import { dataFetchingClient } from 'pricing-pages-ui/utils/dataFetchingClient';
import Routes from 'pricing-pages-ui/Routes';
import { ErrorPageContent } from 'pricing-pages-ui-components/components/ErrorPageContent';
import { DEFAULT } from 'pricing-pages-ui-components/constants/ErrorCauses';
import { CurrencyContextProvider } from 'pricing-pages-ui/context/CurrencyContext';
import { DerivedCohortInfoProvider } from 'pricing-pages-ui/context/DerivedCohortInfoContext';
import { ExperimentProvider } from 'pricing-pages-ui/context/ExperimentContext';
import { GlobalLoadingStateContextProvider } from 'pricing-pages-ui/context/GlobalLoadingStateContext';
import { PublicI18nContext } from 'pricing-pages-ui/context/I18nContext';
import { setupMetricsFactory } from 'pricing-pages-ui/utils/metrics';
import { ProductCacheWarmer } from 'pricing-pages-ui/v2/components/ProductCacheWarmer';
import { ErrorBoundary } from 'pricing-pages-ui/v2/components/error-boundary/ErrorBoundary';
import { PUBLIC_APP_ROOT } from 'pricing-pages-ui/v2/components/error-boundary/ErrorBoundaryNames';
import { ERROR_SEVERITY } from 'pricing-pages-ui/v2/components/error-boundary/ErrorSeverity';
import rhumbConfig from '../rhumb-config.yaml';
import { setupChatTracking } from './setup/setupChatTracking';
import { fetchPublicPricingPageDetails } from './setup/fetchPublicPricingPageDetails';
import { fetchUserData } from './setup/fetchUserData';
import { getManualI18nLang } from './setup/i18nGlobals';
import { prefetchCurrentRouteChunk } from './setup/prefetchCurrentRouteChunk';
import { redirectNonProxied } from './setup/redirectNonProxied';
import { setupEventTracker } from './setup/setupEventTracker';
import { setupRavenFilters } from './setup/setupRavenFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PublicAppErrorFallback = () => /*#__PURE__*/_jsx(Flex, {
  flexDirection: "column",
  alignItems: "center",
  padding: "1000",
  children: /*#__PURE__*/_jsx(ErrorPageContent, {
    errorCause: DEFAULT
  })
});
export async function start(config) {
  // Raven first so any later thrown error flows through our filters.
  setupRavenFilters();
  // Metrics factory before anything that might emit a counter, including
  // redirectNonProxied below.
  setupMetricsFactory('pricing-pages-ui-public-app');

  // Kick off i18n fetch now so it runs in parallel with the other sync
  // boot steps; we only await the result before startRouter / redirect
  // because both need i18n ready.
  const i18nManualLang = getManualI18nLang();
  const langAvailable = BasicPublicI18nSetup.setup({
    lang,
    manualLocaleSettings: i18nManualLang ? {
      locale: i18nManualLang,
      langEnabled: true
    } : undefined
  });

  // Prefetch the current route's lazy chunk alongside i18n so its network
  // and parse cost doesn't run sequentially after langAvailable.
  prefetchCurrentRouteChunk();

  // Non-critical boot steps. Chat tracking defers itself via
  // requestIdleCallback; event tracker is sync and cheap.
  setupChatTracking();
  setupEventTracker();

  // i18n is the only step in the critical path that can realistically
  // reject. Narrow the catch to just that so unrelated errors surface
  // through Raven instead of getting swallowed into an error screen.
  try {
    await langAvailable;
  } catch (error) {
    Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['failed-to-bootstrap-public-app-i18n']
    });
    startRouter(config, BasicPublicI18nSetup.provider, true);
    return;
  }

  // Must run AFTER i18n resolves: getAbsolutePricingPageUrl reads the
  // current language via getI18nLanguage() to pick the correct locale
  // host (hubspot.es, hubspot.de, etc). Running it earlier would always
  // route non-English users to www.hubspot.com.
  if (redirectNonProxied()) {
    return;
  }
  startRouter(config, BasicPublicI18nSetup.provider);
}
export function startRouter(config, i18nLoader, hasError = false) {
  if (hasError) {
    renderRoot( /*#__PURE__*/_jsx(Application, {
      name: "pricing-pages-ui-public-app",
      children: /*#__PURE__*/_jsx(PublicAppErrorFallback, {})
    }));
    return;
  }
  const history = createBrowserHistory({
    basename: config.rootUrl
  });
  fetchUserData();
  fetchPublicPricingPageDetails();
  renderRoot( /*#__PURE__*/_jsx(Application, {
    name: "pricing-pages-ui-public-app",
    children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: /*#__PURE__*/_jsx(ExperimentProvider, {
        children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
          value: {
            i18nLoader: i18nLoader
          },
          children: /*#__PURE__*/_jsx(RhumbProvider, {
            history: historyAdapter(history),
            config: rhumbConfig,
            children: /*#__PURE__*/_jsx(GlobalLoadingStateContextProvider, {
              children: /*#__PURE__*/_jsx(CurrencyContextProvider, {
                children: /*#__PURE__*/_jsx(Router, {
                  history: history,
                  children: /*#__PURE__*/_jsxs(ErrorBoundary, {
                    errorBoundaryName: PUBLIC_APP_ROOT,
                    severity: ERROR_SEVERITY.HIGH,
                    FallbackComponent: PublicAppErrorFallback,
                    children: [/*#__PURE__*/_jsx(ProductCacheWarmer, {}), /*#__PURE__*/_jsx(DerivedCohortInfoProvider, {
                      children: /*#__PURE__*/_jsx(Routes, {})
                    })]
                  })
                })
              })
            })
          })
        })
      })
    })
  }));
}